import React, { useEffect, useState } from 'react';

import { Button, Col, Row, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';

import {
  ATTRIBUTES_SECTIONS,
  ATTRIBUTE_SCORES_GROUP_BY_SECTION,
  ATTRIBUTE_SCORES,
} from 'lib/networking/endpoints';
import AttributeScoringRow from 'components/layout/attribute_scoring/AttributeScoringRow';
import Container from 'components/shared/Container';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import {
  l0BeverageCategoryId,
  l0FoodCategoryId,
  l0OperationalsCategoryId,
} from 'lib/enums/TaxonomyLevel';

function AttributeScoringView() {
  const history = useHistory();
  const [records, setRecords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const { postData: createRecords } = usePost(
    ATTRIBUTE_SCORES,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'created successfully',
      });
      setIsEditing(false);
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      }),
  );

  const { data: { attribute_scores = [] } = {} } = useGet(ATTRIBUTE_SCORES_GROUP_BY_SECTION, {});

  const { data: { data: sections = [] } = [] } = useGet(ATTRIBUTES_SECTIONS, {});

  useEffect(() => {
    if (attribute_scores.length > 0) {
      setRecords(attribute_scores);
    }
  }, [attribute_scores]);

  // Add total calculation
  const totals = records.reduce(
    (acc, record) => {
      if (record.l0_category_id === l0FoodCategoryId) acc.fdTotal += Number(record.max_score) || 0;
      if (record.l0_category_id === l0BeverageCategoryId)
        acc.bevTotal += Number(record.max_score) || 0;
      if (record.l0_category_id === l0OperationalsCategoryId)
        acc.opsTotal += Number(record.max_score) || 0;
      return acc;
    },
    { fdTotal: 0, bevTotal: 0, opsTotal: 0 },
  );

  function handleSectionUpdate(section) {
    const scoreIndex = records.findIndex(rec => rec.attribute_section_id === -1);
    if (scoreIndex !== -1) {
      setRecords(prev => {
        const updatedRecords = [...prev];
        updatedRecords[scoreIndex].attribute_section_id = section.value;
        updatedRecords[scoreIndex].attribute_section = section.label;
        return updatedRecords;
      });
    }
  }

  const handleUpdateRecord = (categoryId, sectionId, maxScore) => {
    const scoreIndex = records.findIndex(
      rec => rec.l0_category_id === categoryId && rec.attribute_section_id === sectionId,
    );
    if (scoreIndex !== -1) {
      setRecords(prev => {
        const updatedRecords = [...prev];
        updatedRecords[scoreIndex].max_score = maxScore;
        return updatedRecords;
      });
    } else {
      const newRecord = {
        l0_category_id: categoryId,
        attribute_section_id: sectionId,
        max_score: maxScore,
      };
      setRecords(prev => [...prev, newRecord]);
    }
  };

  const tableHeaders = (
    <thead>
      <tr>
        <th>Attribute Section</th>
        <th>Max Fd Score</th>
        <th>Max Bev Score</th>
        <th>Max Ops Score</th>
      </tr>
    </thead>
  );

  return (
    <Container className={classNames(css(styles.container), 'mx-3')}>
      <Row>
        <Col>
          <h3 className="font-weight-bold">Attribute Scores</h3>
        </Col>
        <Col className="text-right">
          <Button
            variant="link"
            className="mr-2"
            onClick={() => history.push('/attribute-scoring/sub-category')}
          >
            Set up sub category scores
          </Button>
          <Button
            variant="outline-primary"
            className="mr-2"
            onClick={() => setIsEditing(!isEditing)}
          >
            Edit
          </Button>
          <Button
            className="mr-2"
            onClick={() => {
              setRecords([
                ...records,
                {
                  l0_category_id: '',
                  attribute_section_id: -1, // -1 is to identify as a new row
                  max_score: 0,
                },
              ]);
              setIsEditing(true);
            }}
          >
            Add Attribute Score
          </Button>
          <Button
            onClick={() => {
              createRecords({ data: records });
            }}
          >
            Save
          </Button>
        </Col>
      </Row>
      <Table hover className="mt-3">
        {tableHeaders}
        <tbody>
          {[...new Set(records.map(score => score.attribute_section_id))].map(id => (
            <AttributeScoringRow
              key={id}
              scoreRecords={records.filter(record => record.attribute_section_id === id)}
              onUpdate={handleUpdateRecord}
              handleSectionUpdate={handleSectionUpdate}
              isEditing={isEditing}
              sections={sections}
            />
          ))}
          <tr className="font-weight-bold">
            <td>Total</td>
            <td className="pl-4">{totals.fdTotal}</td>
            <td className="pl-4">{totals.bevTotal}</td>
            <td className="pl-4">{totals.opsTotal}</td>
          </tr>
        </tbody>
      </Table>
    </Container>
  );
}

export const styles = StyleSheet.create({
  container: {
    minHeight: 'calc(100vh - 150px)',
  },
});

export default AttributeScoringView;
